import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getLastViewedTopicId } from '@/features/topics/state/useActiveTopic'
import { getTopicDetail, updateTopicList } from '@/features/topics/api/topicsClient'
import type { TopicList } from '@/features/topics/model/contracts'

interface FormOverrides {
  name?: string
  slug?: string
  isPublic?: boolean
}

export function useSettingsGeneralController() {
  const queryClient = useQueryClient()
  const topicId = getLastViewedTopicId()

  const {
    data: topic,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['topics', 'detail', topicId],
    queryFn: ({ signal }) => getTopicDetail(topicId!, signal),
    enabled: topicId != null,
  })

  const [overrides, setOverrides] = useState<FormOverrides>({})
  const [baseTopicId, setBaseTopicId] = useState<string | null>(null)
  const [savedOnce, setSavedOnce] = useState(false)

  if (topic && topic.id !== baseTopicId) {
    setOverrides({})
    setBaseTopicId(topic.id)
    setSavedOnce(false)
  }

  const name = overrides.name ?? topic?.name ?? ''
  const slug = overrides.slug ?? topic?.slug ?? ''
  const isPublic = overrides.isPublic ?? topic?.isPublic ?? false

  const isDirty =
    topic != null &&
    (name !== topic.name || slug !== topic.slug || isPublic !== topic.isPublic)

  const mutation = useMutation({
    mutationFn: (input: { name: string; slug: string; isPublic: boolean }) =>
      updateTopicList({ id: topicId!, ...input }),
    onSuccess: (updated: TopicList) => {
      queryClient.invalidateQueries({ queryKey: ['topics'] })
      queryClient.setQueryData(['topics', 'detail', topicId], updated)
      setOverrides({})
      setSavedOnce(true)
    },
  })

  function setName(value: string) {
    setOverrides((prev) => ({ ...prev, name: value }))
    setSavedOnce(false)
  }

  function setSlug(value: string) {
    setOverrides((prev) => ({ ...prev, slug: value }))
    setSavedOnce(false)
  }

  function setIsPublic(value: boolean) {
    setOverrides((prev) => ({ ...prev, isPublic: value }))
    setSavedOnce(false)
  }

  function submit() {
    if (topicId && isDirty) {
      mutation.mutate({ name, slug, isPublic })
    }
  }

  return {
    topicId,
    topic,
    form: { name, slug, isPublic },
    isLoading,
    error,
    isDirty,
    isSaving: mutation.isPending,
    savedOnce,
    setName,
    setSlug,
    setIsPublic,
    submit,
  }
}
